import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Drawer,
  Fade,
  FormControl,
  FormControlLabel,
  InputLabel,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import { useDisagreementReviewSession } from '../../hooks/useDisagreementReviewSession';
import { evalRunForCriterion, useJudgementSource } from '../../hooks/useJudgementSource';
import { useNotification } from '../../hooks/useNotification';
import { readReviewLocation, writeReviewLocation } from '../../reviewLocation';
import {
  fetchCriteria,
  fetchDisagreementCandidates,
  fetchDisagreementCounts,
  fetchUsers,
} from '../../services/api';
import { labelColors } from '../../theme';
import {
  type ConsensusMode,
  type Criteria,
  type DisagreementCandidate,
  type DisagreementCount,
  type DisagreementReviewOmissions,
  REVIEWED_CLASSIFICATIONS,
  type ReviewedClassification,
  type User,
} from '../../types';
import ConsensusModeToggle, { DEFAULT_CONSENSUS_MODE } from '../common/ConsensusModeToggle';
import JudgementSourceSelector from '../common/JudgementSourceSelector';
import ReviewFilterEmptyState from '../common/ReviewFilterEmptyState';
import {
  disagreementCandidatesEmptyCopy,
  disagreementLocationEmptyCopy,
} from '../common/reviewEmptyState';
import SearchableSelect from '../common/SearchableSelect';
import TranscriptionNav from '../common/TranscriptionNav';
import EssayContent from '../EssayContent';
import DisagreementCandidateSummary from './DisagreementCandidateSummary';

const omissionsTooltip = (omissions: DisagreementReviewOmissions) =>
  `Counts omit ${omissions.unlinkedTranscriptions} unlinked transcription${
    omissions.unlinkedTranscriptions === 1 ? '' : 's'
  } and ${omissions.conflictingPapers} paper${
    omissions.conflictingPapers === 1 ? '' : 's'
  } with conflicting production links and ${omissions.unresolvedEntities ?? 0} unresolved ${
    (omissions.unresolvedEntities ?? 0) === 1 ? 'entity' : 'entities'
  }.`;

const DisagreementReview: React.FC = () => {
  const [criteriaList, setCriteriaList] = useState<Criteria[]>([]);
  const [showArchived, setShowArchived] = useState(false);
  const [selectedCriteriaId, setSelectedCriteriaId] = useState(
    () => readReviewLocation().criteriaId
  );
  const [requestedTranscriptionId, setRequestedTranscriptionId] = useState(
    () => readReviewLocation().transcriptionId
  );
  const [loadingCriteria, setLoadingCriteria] = useState(true);

  const [users, setUsers] = useState<User[]>([]);
  const [counts, setCounts] = useState<Record<string, DisagreementCount>>({});
  const [omissions, setOmissions] = useState<DisagreementReviewOmissions>({
    unlinkedTranscriptions: 0,
    conflictingPapers: 0,
    unresolvedEntities: 0,
  });
  const [loadingCounts, setLoadingCounts] = useState(true);
  const [consensusMode, setConsensusMode] = useState<ConsensusMode>(DEFAULT_CONSENSUS_MODE);
  const [currentPolicyOnly, setCurrentPolicyOnly] = useState(true);
  const [policyOpen, setPolicyOpen] = useState(false);
  const {
    source,
    setKind,
    evalRuns,
    productionFrom,
    productionTo,
    loadingEvalRuns,
    evalRunsError,
    usedProductionFallback,
    isReady,
  } = useJudgementSource();

  const { notification, setNotification, hideNotification } = useNotification(3000);

  const countsRequestIdRef = useRef(0);

  const reloadCounts = useCallback(() => {
    if (!isReady) {
      setCounts({});
      setOmissions({
        unlinkedTranscriptions: 0,
        conflictingPapers: 0,
        unresolvedEntities: 0,
      });
      setLoadingCounts(false);
      return;
    }
    const requestId = ++countsRequestIdRef.current;
    setLoadingCounts(true);
    fetchDisagreementCounts(consensusMode, currentPolicyOnly, source)
      .then(({ counts: data, omissions: nextOmissions }) => {
        if (countsRequestIdRef.current !== requestId) return;
        const map: Record<string, DisagreementCount> = {};
        for (const c of data) map[c.criteriaId] = c;
        setCounts(map);
        setOmissions(nextOmissions);
        setLoadingCounts(false);
      })
      .catch(() => {
        if (countsRequestIdRef.current !== requestId) return;
        setCounts({});
        setOmissions({
          unlinkedTranscriptions: 0,
          conflictingPapers: 0,
          unresolvedEntities: 0,
        });
        setLoadingCounts(false);
      });
  }, [consensusMode, currentPolicyOnly, isReady, source]);

  useEffect(() => {
    fetchCriteria().then(data => {
      setCriteriaList(data);
      setLoadingCriteria(false);
    });
    fetchUsers().then(setUsers);
    reloadCounts();
  }, [reloadCounts]);

  const fetchCandidatesForSession = useCallback(
    (criteriaId: string) =>
      fetchDisagreementCandidates(criteriaId, consensusMode, currentPolicyOnly, source, true),
    [consensusMode, currentPolicyOnly, source]
  );

  const unknownCriterion =
    Boolean(selectedCriteriaId) &&
    !loadingCriteria &&
    !criteriaList.some(criterion => criterion.id === selectedCriteriaId);

  const session = useDisagreementReviewSession<DisagreementCandidate>({
    selectedCriteriaId,
    criteriaList,
    enabled: isReady && !loadingCriteria && !unknownCriterion,
    judgementSource: source,
    requestedTranscriptionId,
    fetchCandidates: fetchCandidatesForSession,
    onCandidatesError: () =>
      setNotification({
        open: true,
        message:
          source.kind === 'eval'
            ? 'Could not load disagreement candidates for this Eval Run.'
            : 'Could not load disagreement candidates. The production judgement database may be unavailable.',
        severity: 'error',
      }),
    onSubmitError: () =>
      setNotification({
        open: true,
        message: 'Failed to save review. Please try again.',
        severity: 'error',
      }),
    onSubmitSuccess: ({ wasUpdate }) =>
      setNotification({
        open: true,
        message: wasUpdate ? 'Review updated' : 'Review saved',
        severity: 'success',
      }),
    onUnresolvedCountChange: ({ criteriaId, delta }) => {
      setCounts(prev => {
        const existing = prev[criteriaId];
        if (!existing) return prev;
        return {
          ...prev,
          [criteriaId]: { ...existing, unresolved: existing.unresolved + delta },
        };
      });
    },
    getTranscriptionId: c => c.transcriptionId,
    getEntityText: c => c.sentence,
    getPaperNumber: c => c.paperNumber,
    getDisplayLabel: c => c.humanLabel,
    getAlreadyReviewed: c => c.alreadyReviewed,
    getReview: c => c.review,
    getOriginalForSubmit: c => c.aiLabel,
    applyOptimisticReview: (c, review) => ({ ...c, alreadyReviewed: true, review }),
    getHasDisagreement: c => c.hasDisagreement !== false,
    createFallbackCandidate: ({ transcriptionId, fragment, index, judgement, paperNumber }) => {
      const entity = judgement?.entities?.[index] ?? judgement?.sentences?.[index];
      const aiLabel = (entity?.label ?? 'no attempt') as ReviewedClassification;
      return {
        transcriptionId: transcriptionId || requestedTranscriptionId || '',
        paperNumber: paperNumber ?? 0,
        sentence: fragment,
        granularity: judgement?.granularity ?? 'sentence',
        humanLabel: 'no attempt',
        humanReviewerCount: 0,
        humanReasons: [],
        aiLabel,
        aiReason: entity?.reason || '',
        groundTruthSource: 'label_consensus',
        previousReview: null,
        alreadyReviewed: false,
        review: null,
        hasDisagreement: false,
      };
    },
  });

  const criteriaOptions = useMemo(
    () =>
      criteriaList
        .filter(c => showArchived || !c.isArchived || c.id === selectedCriteriaId)
        .map(c => {
          const count = counts[c.id];
          const countText = count
            ? `${count.unresolved}${count.unresolved !== count.total ? `/${count.total}` : ''}`
            : '0';
          return {
            value: c.id,
            label: c.name,
            unresolved: count?.unresolved ?? 0,
            renderContent: (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <Box component="span" sx={{ flex: 1 }}>
                  {c.name} [{c.granularity}]{c.isArchived ? ' (Archived)' : ''}
                </Box>
                {loadingCounts ? (
                  <CircularProgress size={12} />
                ) : (
                  <Box component="span" sx={{ color: 'text.secondary' }}>
                    ({countText})
                  </Box>
                )}
              </Box>
            ),
          };
        })
        .sort((a, b) => {
          if (b.unresolved !== a.unresolved) return b.unresolved - a.unresolved;
          return a.label.localeCompare(b.label);
        })
        .map(({ value, label, renderContent }) => ({ value, label, renderContent })),
    [criteriaList, counts, loadingCounts, showArchived, selectedCriteriaId]
  );

  const usersById = useMemo(() => {
    const map: Record<string, User> = {};
    for (const u of users) map[u.id] = u;
    return map;
  }, [users]);

  const reviewerName = (userId: string) => {
    const u = usersById[userId];
    return u ? `${u.firstName} ${u.lastName}` : userId.slice(0, 8);
  };

  const {
    selectedTranscriptionId,
    setSelectedTranscriptionId,
    judgement,
    loadingJudgement,
    activePolicy,
    loadingPolicy,
    editType,
    setEditType,
    editReason,
    setEditReason,
    submitting,
    transcriptionOptions,
    fragments,
    selectedCandidate,
    selectedFragmentIndex,
    unresolvedTotal,
    loadingCandidates,
    requestedTranscriptionMissing,
    handleFragmentClick,
    getLabelTypeForFragment,
    isFragmentReviewed,
    handleSubmit,
    goToPreviousLabel,
    goToNextLabel,
    disagreementLabelPosition,
    disagreementLabelCount,
  } = session;

  useEffect(() => {
    writeReviewLocation(selectedCriteriaId, selectedTranscriptionId || requestedTranscriptionId);
  }, [selectedCriteriaId, selectedTranscriptionId, requestedTranscriptionId]);

  const selectedGranularity =
    judgement?.granularity ??
    criteriaList.find(criterion => criterion.id === selectedCriteriaId)?.granularity ??
    'sentence';

  const emptyCopy = disagreementCandidatesEmptyCopy({
    currentPolicyOnly,
    consensusMode,
    includeAny: true,
    sourceKind: source.kind,
    evalRunCoversCriterion: Boolean(evalRunForCriterion(evalRuns, selectedCriteriaId || null)),
    comparedCount: counts[selectedCriteriaId]?.comparedCount ?? 0,
    scope: 'criterion',
  });

  const hasOmissions =
    omissions.unlinkedTranscriptions > 0 ||
    omissions.conflictingPapers > 0 ||
    (omissions.unresolvedEntities ?? 0) > 0;
  const criterionCount = selectedCriteriaId ? counts[selectedCriteriaId] : undefined;
  const reviewTotal = criterionCount?.total ?? 0;
  const resolvedPercent =
    reviewTotal > 0
      ? Math.max(0, Math.round(((reviewTotal - unresolvedTotal) / reviewTotal) * 100))
      : 0;

  return (
    <Box
      sx={{
        bgcolor: '#f4f6f8',
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 80px)',
        minHeight: 620,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ bgcolor: 'white', borderBottom: '1px solid #dfe3e8', px: 3, py: 1 }}>
        <Typography variant="h4" component="h1">
          Resolve Disagreements
        </Typography>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          useFlexGap
          flexWrap="wrap"
          sx={{ mt: 1 }}
        >
          <FormControl size="small" sx={{ flex: 1, minWidth: 220 }}>
            <InputLabel>Criterion</InputLabel>
            <SearchableSelect
              value={selectedCriteriaId}
              label="Criterion"
              onChange={e => {
                setSelectedCriteriaId(e.target.value as string);
                setRequestedTranscriptionId('');
              }}
              disabled={loadingCriteria}
              options={criteriaOptions}
            />
          </FormControl>
          <JudgementSourceSelector
            source={source}
            evalRuns={evalRuns}
            productionFrom={productionFrom}
            productionTo={productionTo}
            loadingEvalRuns={loadingEvalRuns}
            evalRunsError={evalRunsError}
            usedProductionFallback={usedProductionFallback}
            selectedCriteriaId={selectedCriteriaId}
            onKindChange={setKind}
            compact
          />
          <ConsensusModeToggle
            value={consensusMode}
            onChange={setConsensusMode}
            includeAny
            compact
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={currentPolicyOnly}
                onChange={e => setCurrentPolicyOnly(e.target.checked)}
              />
            }
            label="Current policy only"
            sx={{ mr: 0, whiteSpace: 'nowrap' }}
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={showArchived}
                onChange={e => setShowArchived(e.target.checked)}
              />
            }
            label="Show archived"
            sx={{ mr: 0, whiteSpace: 'nowrap' }}
          />
          {hasOmissions && (
            <Tooltip title={omissionsTooltip(omissions)}>
              <Box
                component="span"
                aria-label="Review count omissions"
                tabIndex={0}
                sx={{ display: 'inline-flex', alignItems: 'center' }}
              >
                <WarningAmberIcon color="warning" fontSize="small" />
              </Box>
            </Tooltip>
          )}
        </Stack>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          minHeight: 0,
          overflow: 'hidden',
          pl: 3,
          pr: 8,
          pb: 2,
          pt: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            maxWidth: 920,
            minHeight: 0,
            mx: 'auto',
            position: 'relative',
            width: '100%',
          }}
        >
          <Fade in={notification.open}>
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 10,
              }}
            >
              <Alert onClose={hideNotification} severity={notification.severity}>
                {notification.message}
              </Alert>
            </Box>
          </Fade>

          {selectedCriteriaId && !unknownCriterion && (
            <Box sx={{ flexShrink: 0 }}>
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }} useFlexGap>
                <Box sx={{ flex: '1 1 280px', minWidth: 240 }}>
                  <TranscriptionNav
                    value={selectedTranscriptionId}
                    options={transcriptionOptions.map(o => ({ value: o.value, label: o.label }))}
                    disabled={loadingCandidates || transcriptionOptions.length === 0}
                    onChange={setSelectedTranscriptionId}
                    onPrevious={goToPreviousLabel}
                    onNext={goToNextLabel}
                    iconButtons
                    labelPosition={disagreementLabelPosition}
                    labelCount={disagreementLabelCount}
                  />
                </Box>
                <Box sx={{ flex: 1, minWidth: 130, mx: 1 }}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="caption" fontWeight={700}>
                      {unresolvedTotal} unresolved
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {resolvedPercent}%
                    </Typography>
                  </Stack>
                  <LinearProgress variant="determinate" value={resolvedPercent} />
                </Box>
              </Stack>
              {judgement?.policyId && activePolicy && judgement.policyId !== activePolicy.id && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  AI judgement was made using an older policy version
                </Alert>
              )}
              {judgement && (judgement.excludedEntityCount ?? 0) > 0 && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  {judgement.excludedEntityCount ?? 0}{' '}
                  {judgement.excludedEntityCount === 1 ? 'entity was' : 'entities were'} excluded
                  because they could not be resolved safely.
                </Alert>
              )}
              {(judgement?.entityWarnings ?? []).map((warning, index) => (
                <Alert key={`${warning}-${index}`} severity="warning" sx={{ mt: 1 }}>
                  {warning}
                </Alert>
              ))}
            </Box>
          )}

          <Box
            sx={{
              display: 'flex',
              flex: 1,
              flexDirection: 'column',
              minHeight: 0,
              mt: selectedCriteriaId && !unknownCriterion ? 2 : 0,
            }}
          >
            {loadingCandidates ||
            loadingJudgement ||
            (Boolean(selectedCriteriaId) && loadingCriteria) ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : !isReady ? (
              <Typography color="text.secondary" sx={{ pt: 2, textAlign: 'center' }}>
                Select an Eval Run to compare AI Judgements
              </Typography>
            ) : unknownCriterion ? (
              <ReviewFilterEmptyState {...disagreementLocationEmptyCopy('unknownCriterion')} />
            ) : !selectedCriteriaId ? (
              <Typography color="text.secondary" sx={{ pt: 2, textAlign: 'center' }}>
                Select a criterion to begin
              </Typography>
            ) : transcriptionOptions.length === 0 ? (
              <ReviewFilterEmptyState title={emptyCopy.title} hint={emptyCopy.hint} />
            ) : requestedTranscriptionMissing ? (
              <ReviewFilterEmptyState {...disagreementLocationEmptyCopy('unknownTranscription')} />
            ) : fragments.length > 0 ? (
              <Paper
                variant="outlined"
                sx={{
                  display: 'flex',
                  flex: 1,
                  flexDirection: 'column',
                  minHeight: 0,
                  overflow: 'hidden',
                  p: 2.5,
                  position: 'relative',
                }}
              >
                <Box
                  key={`${selectedTranscriptionId}-${selectedFragmentIndex}`}
                  aria-hidden
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    pointerEvents: 'none',
                    zIndex: 1,
                    bgcolor: 'background.paper',
                    '@keyframes disagreementTranscriptWash': {
                      from: { opacity: 0.55 },
                      to: { opacity: 0 },
                    },
                    animation: 'disagreementTranscriptWash 450ms ease-out forwards',
                    '@media (prefers-reduced-motion: reduce)': {
                      animation: 'none',
                      display: 'none',
                    },
                  }}
                />
                <EssayContent
                  fragments={fragments}
                  selectedFragmentIndex={selectedFragmentIndex}
                  handleFragmentClick={handleFragmentClick}
                  getLabelTypeForFragment={getLabelTypeForFragment}
                  isFragmentReviewed={isFragmentReviewed}
                  granularity={selectedGranularity}
                  emphasizeSelected
                />
              </Paper>
            ) : (
              <Typography color="text.secondary" sx={{ pt: 2, textAlign: 'center' }}>
                No {selectedGranularity} entities returned for this transcription
              </Typography>
            )}
          </Box>

          {selectedCandidate && (
            <Box sx={{ flexShrink: 0, mt: 1.5 }}>
              <DisagreementCandidateSummary
                candidate={selectedCandidate}
                reviewerName={reviewerName}
              />

              {selectedCandidate.hasDisagreement !== false && (
                <Paper elevation={2} sx={{ p: 1.25 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                      mb: 0.75,
                    }}
                  >
                    <Typography variant="subtitle2">Resolution</Typography>
                    {selectedCandidate.previousReview && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          const review = selectedCandidate.previousReview!;
                          setEditType(review.correctedClassification);
                          setEditReason(review.reason);
                        }}
                      >
                        Copy from previous review
                      </Button>
                    )}
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'stretch',
                      gap: 1.25,
                    }}
                  >
                    <ToggleButtonGroup
                      value={editType}
                      exclusive
                      orientation="vertical"
                      onChange={(_e, val) => {
                        if (val !== null) setEditType(val);
                      }}
                      sx={{
                        flexShrink: 0,
                        '& .MuiToggleButton-root': {
                          justifyContent: 'flex-start',
                          minWidth: '8.5rem',
                          px: 1.5,
                          py: 0.5,
                          fontSize: '0.875rem',
                          lineHeight: 1.3,
                        },
                      }}
                    >
                      {REVIEWED_CLASSIFICATIONS.map(type => (
                        <ToggleButton
                          key={type}
                          value={type}
                          sx={{
                            '&.Mui-selected': {
                              bgcolor: labelColors[type].main,
                              borderColor: labelColors[type].border,
                            },
                          }}
                        >
                          {type}
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>

                    <TextField
                      size="small"
                      multiline
                      minRows={2}
                      maxRows={4}
                      placeholder="Reason for this call..."
                      value={editReason}
                      onChange={e => setEditReason(e.target.value)}
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        '& .MuiInputBase-root': {
                          height: '100%',
                          alignItems: 'flex-start',
                        },
                        '& .MuiInputBase-input': {
                          fontSize: '0.875rem',
                          lineHeight: 1.43,
                        },
                      }}
                    />
                    <Button
                      variant="contained"
                      size="small"
                      disabled={!editType || !editReason.trim() || !activePolicy || submitting}
                      onClick={handleSubmit}
                      sx={{ alignSelf: 'flex-end', flexShrink: 0 }}
                    >
                      {selectedCandidate.alreadyReviewed ? 'Update review' : 'Submit review'}
                    </Button>
                  </Box>
                </Paper>
              )}
            </Box>
          )}
        </Box>
      </Box>

      <Button
        variant="contained"
        aria-label="Open policy"
        onClick={() => setPolicyOpen(true)}
        sx={{
          alignItems: 'center',
          borderRadius: '20px 0 0 20px',
          boxShadow: 4,
          flexDirection: 'column',
          gap: 0.5,
          minWidth: 44,
          px: 0.75,
          py: 1.25,
          position: 'fixed',
          right: 0,
          top: '42%',
          zIndex: 10,
        }}
      >
        <MenuBookOutlinedIcon fontSize="small" />
        <Box
          component="span"
          sx={{
            fontSize: 11,
            lineHeight: 1,
            writingMode: 'vertical-rl',
            transform: 'rotate(180deg)',
          }}
        >
          Policy
        </Box>
      </Button>
      <Drawer
        anchor="right"
        open={policyOpen}
        onClose={() => setPolicyOpen(false)}
        transitionDuration={0}
      >
        <Box sx={{ p: 3, width: 560 }}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}
          >
            <Typography variant="h6">Policy reference</Typography>
            <Button aria-label="Close policy" onClick={() => setPolicyOpen(false)}>
              Close
            </Button>
          </Box>
          {loadingPolicy ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : activePolicy ? (
            <Box className="markdown-container" sx={{ p: 0, height: 'auto' }}>
              <ReactMarkdown remarkPlugins={[remarkBreaks]}>{activePolicy.text}</ReactMarkdown>
            </Box>
          ) : (
            <Typography color="text.secondary">
              {selectedCriteriaId
                ? 'No policy found for this criterion'
                : 'Select a criterion to view its policy'}
            </Typography>
          )}
        </Box>
      </Drawer>
    </Box>
  );
};

export default DisagreementReview;
